/**
 * Single source of truth for all supported locales across the monorepo.
 *
 * When adding a new language:
 *   1. Add an entry to LOCALE_DEFINITIONS
 *   2. Create the .po file: apps/web/locales/{code}/messages.po
 *   3. Run: pnpm lingui:extract && pnpm lingui:compile
 *   4. Import the compiled messages in the web & extension i18n-providers
 *
 * ⚠️ The Spanish locale code is `es` PLAIN, never `es-VE`, and that's not an oversight:
 * `i18n-provider.tsx` resolves the device's language with
 * `navigator.language.split("-")[0]`, so `es-VE`, `es-MX`, `es-ES` and `es-419`
 * all fall into `es`. With a catalog tagged `es-VE` NONE of them would match and
 * everyone would see the app in English. This package's test also requires `^[a-z]{2,3}$`.
 * Venezuelan vocabulary goes INSIDE the `es` catalog, not in its tag —
 * and per-institution-type variants belong in the vocabulary profiles
 * (`apps/web/src/i18n/vocabulary/`), not in more locales.
 *
 * 2026-08-14: from 11 locales down to 2. The product is for Venezuela and the other 9
 * (pt, fr, de, ko, ja, zh, hi, ar, he) had no audience. That also took with it
 * the only RTL pair (ar, he): `RTL_LOCALES` stays EMPTY on purpose, and RTL
 * support is kept (6 lines) because adding Arabic back would be trivial, while
 * removing it would, in practice, be irreversible.
 */
export const LOCALE_DEFINITIONS = [
  { code: "en", flag: "🇺🇸", label: "English", englishLabel: "English", isRTL: false },
  { code: "es", flag: "🇻🇪", label: "Español", englishLabel: "Spanish", isRTL: false },
] as const;

/** Locale code union derived from LOCALE_DEFINITIONS. */
export type SupportedLocale = (typeof LOCALE_DEFINITIONS)[number]["code"];

/** All locale codes derived from LOCALE_DEFINITIONS — runtime array. */
export const SUPPORTED_LOCALES: SupportedLocale[] = LOCALE_DEFINITIONS.map((l) => l.code);

/** Set of RTL locale codes derived from LOCALE_DEFINITIONS. */
export const RTL_LOCALES = new Set<SupportedLocale>(
  LOCALE_DEFINITIONS.filter((l) => l.isRTL).map((l) => l.code),
);

/** The locale to use when no stored preference or device locale is matched. */
export const DEFAULT_LOCALE: SupportedLocale = "en";
